import React, { useEffect, useState } from 'react'
import SEO from 'src/components/common/SEO'

import HeroLiveFeed from 'src/components/common/HeroLivePlayer'
import { useSelector } from 'react-redux'
import { selectParams } from 'src/modules/reducers/params'
import { selectCountryCode } from 'src/modules/reducers/market'
import { Helmet } from 'react-helmet'
import {
  ChannelSection,
  DealSection,
  FAQSection,
  HighlightSection,
  Layout,
  MatchTicker,
  NetworkSection,
  PlatformSection,
  RedzoneBanner,
  RSNGeolocationBanner,
} from 'src/components/common'
import { useIsTrial } from 'src/modules/hooks/useIsTrial'
import HeaderSignIn from 'src/components/common/HeaderSignIn'
import { WEB_MARKETING_HEADER_SIGN_IN } from 'src/modules/reducers/cohorts/constants'
import { useCohortFlag } from 'src/modules/hooks/useCohortFlag'

const LiveTemplateDefault = ({
  lang = 'en',
  market = 'us',
  // Offer Code
  offerCode,
  offerBanner,
  // Metadata
  pageTitle,
  pageDescription,
  // Hero Section
  logo,
  logoHeight,
  title,
  subtitle,
  ctaText,
  background,
  callsign,
  forceCallsign,
  loadingText,
  // Optional Highlight Section
  highlightImage,
  highlightHeadline,
  highlightSubtitle,
  highlightNote,
  highlightBackground,
  highlightUrlParams,
  highlightCtaText,
  // Pass Network Name to Live Preview
  networkName,
  // Network Section
  networkHeadline,
  networkSubtitle,
  networkSet,
  customNetworks,
  // Others
  team,
  // AB Test for RSN
  showRSN,
  showRedzone,
  //  Fox Redirect
  foxRedirect,
  // Embedded Gamefinder
  gamefinder,
  // Remove references to cable
  removeCable,
  // AB Test for MatchTicker
  matchtickerSportId,
  matchtickerLeagueName,
  matchtickerLeagueId,
  // Disable Free Trial
  disableFreeTrial,
  // CA Redirect URL
  caRedirectUrl,
  // Personalization Params
  sportId,
  leagueId,
  teamId,
}) => {
  const isTrial = useIsTrial(market)
  const showHeaderSignIn = useCohortFlag(WEB_MARKETING_HEADER_SIGN_IN)
  // Disable Free Trial Modifications
  disableFreeTrial = disableFreeTrial ?? !isTrial
  const modifiedCtaText = ctaText
  const modifiedNetworkSubtitle =
    networkSubtitle ||
    (disableFreeTrial && (lang === 'es' ? 'Cancela cuando quieras.' : 'Cancel online anytime.'))

  let metaDescription =
    pageDescription ||
    '200+ live channels. Free Cloud DVR. Watch on your phone, Roku, Apple TV, Amazon Fire TV, computer & more. FOX, CBS, NFL Network, SHOWTIME, beIN SPORTS & more.'
  let heroTitle = title || (lang === 'es' ? '' : 'Watch Live Sports & TV Without Cable')

  const params = useSelector(selectParams)
  const countryCode = useSelector(selectCountryCode)

  const [pageSlug, setPageSlug] = useState(null)

  useEffect(() => {
    if (callsign) {
      setPageSlug(callsign)
    } else if (countryCode === 'CA' || countryCode === 'CAN') {
      setPageSlug('BEINCHD')
    } else if (params?.page_slug) {
      setPageSlug(params?.page_slug)
    } else if (!forceCallsign) {
      setPageSlug('FS1')
    }
  }, [params?.page_slug, countryCode, callsign])

  return (
    <Layout
      lang={lang}
      market={market}
      offerCode={offerCode}
      offerBanner={offerBanner}
      caRedirectUrl={caRedirectUrl}
      sportId={sportId}
      leagueId={leagueId}
      teamId={teamId}
    >
      <Helmet>
        <link rel="preconnect" crossOrigin="anonymous" href="https://preview.fubo.tv" />
        <link rel="dns-prefetch" crossOrigin="anonymous" href="https://preview.fubo.tv" />
      </Helmet>
      <SEO lang={lang} market={market} title={pageTitle || title} description={metaDescription} />
      {showHeaderSignIn && (
        <HeaderSignIn
          lang={lang}
          market={market}
          disableFreeTrial={disableFreeTrial}
          ctaText={modifiedCtaText}
        />
      )}
      {matchtickerLeagueId && (
        <MatchTicker
          lang={lang}
          market={market}
          sportId={matchtickerSportId}
          leagueName={matchtickerLeagueName}
          leagueId={matchtickerLeagueId}
          disableFreeTrial={disableFreeTrial}
        />
      )}
      <HeroLiveFeed
        lang={lang}
        market={market}
        callsign={pageSlug}
        title={heroTitle}
        subTitle={subtitle}
        background={background}
        ctaText={modifiedCtaText}
        subhead={
          disableFreeTrial &&
          (lang === 'es' ? 'Sigue disfrutando con Fubo.' : 'Keep watching with Fubo.')
        }
        logo={logo}
        logoHeight={logoHeight}
        networkName={networkName}
        loadingText={loadingText}
        disableFreeTrial={disableFreeTrial}
      />
      {highlightImage && (
        <HighlightSection
          image={highlightImage}
          title={highlightHeadline}
          subtitle={highlightSubtitle}
          miceText={highlightNote}
          background={highlightBackground}
          disableFreeTrial={disableFreeTrial}
          urlParams={highlightUrlParams}
          ctaText={highlightCtaText}
        />
      )}
      {showRedzone && (
        <RedzoneBanner ctaText={modifiedCtaText} disableFreeTrial={disableFreeTrial} />
      )}
      {showRSN && (
        <RSNGeolocationBanner
          ctaText={modifiedCtaText}
          disableFreeTrial={disableFreeTrial}
          lang={lang}
          market={market}
        />
      )}
      <NetworkSection
        lang={lang}
        market={market}
        networkHeadline={networkHeadline}
        subtitle={modifiedNetworkSubtitle}
        networkSet={networkSet}
        customNetworks={customNetworks}
        ctaText={modifiedCtaText}
        disableFreeTrial={disableFreeTrial}
      />
      <ChannelSection
        lang={lang}
        market={market}
        title={
          foxRedirect
            ? 'Watch the channels you love'
            : removeCable
              ? 'Enjoy the sports, shows, news and movies you love'
              : null
        }
        disableFreeTrial={disableFreeTrial}
      />
      <DealSection
        lang={lang}
        market={market}
        title={
          foxRedirect ? 'How is Fubo better?' : removeCable ? 'Why you should choose Fubo' : null
        }
      />
      <PlatformSection
        lang={lang}
        market={market}
        ctaText={modifiedCtaText}
        disableFreeTrial={disableFreeTrial}
      />
      <FAQSection
        lang={lang}
        market={market}
        team={team}
        foxRedirect={foxRedirect}
        removeCable={removeCable}
        disableFreeTrial={disableFreeTrial}
      />
    </Layout>
  )
}

export default LiveTemplateDefault
